import React, { useCallback, useMemo, useState } from "react";
import { LeftOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { Form, Input, Select, Button } from "../../../../components/shared";
import { formatWholeNumberInput } from "../../../CommonComponents/FormattedInput/FormattedInput";
import WizardStepper from "../../../shared/WizardStepper/WizardStepper";
import FeatureFlagGuard from "../../../FeatureFlagGuard/FeatureFlagGuard";
import { SIDER_TABS } from "../../../../utils/Constants";
import { NAVIGATION_ROUTES } from "../../../../utils/NavigationUtils";
import { AppStrings } from "../../../../utils/constants/AppStrings";
import { usePageTitle } from "../../../CommonComponents/Layout/PageTitleContext";
import { LOOKBACK_WINDOW_DAYS_LABEL, fieldOperatorOptions } from "../constants";
import EventPropertyField from "../controls/EventPropertyField";
import {
  displayNameFor,
  fieldsForEvent,
  toGroupedSelectOptions,
  withoutCampaignStart,
} from "../eventCatalog";
import { useCreateSegment } from "../../../../queries/dynamicBonuses/useCreateSegment";
import { useDynamicBonusEvents } from "../../../../queries/dynamicBonuses/useDynamicBonusEvents";
import type { SegmentIn, FieldOperator } from "../types";
import wizardStyles from "../../../Affiliates/NewAffiliate/NewAffiliate.module.css";
import styles from "../stylesDynamicBonuses.module.css";

type SummaryValues = {
  name?: string;
  membership_ttl_days?: string;
};

type SegmentStep = "details" | "membershipRule";

const getSteps = (): ReadonlyArray<{ id: SegmentStep; label: string }> => [
  { id: "details", label: AppStrings.DETAILS },
  {
    id: "membershipRule",
    label: AppStrings.PAM_SEGMENTS_WIZARD_STEP_MEMBERSHIP_RULE,
  },
];

type FormValues = {
  name: string;
  description?: string;
  membership_ttl_days?: string;
  event_key?: string;
  field_check_field?: string;
  field_check_operator?: string;
  field_check_value?: string;
};

const getEmpty = (): string => AppStrings.PAM_DYNAMIC_BONUS_EMPTY_VALUE;

const CreateSegmentPage: React.FC = () => {
  usePageTitle(AppStrings.PAM_SEGMENTS_CREATE_TITLE);

  const navigate = useNavigate();
  const [form] = Form.useForm<FormValues>();
  const [summary, setSummary] = useState<SummaryValues>({});

  const [activeStep, setActiveStep] = useState<SegmentStep>("details");
  const [completedSteps, setCompletedSteps] = useState<Set<SegmentStep>>(new Set());

  const watchedEventKey = Form.useWatch("event_key", form) as string | undefined;

  const {
    data: eventGroups = [],
    isLoading: eventsLoading,
    isError: eventsError,
    refetch: refetchEvents,
  } = useDynamicBonusEvents();
  // Full catalog is valid for segments — CRM dispatches to evaluate_segments
  // (BONENG-915). campaign_start is schedule-injected, so strip it like the
  // campaign wizard picker.
  const eventSelectOptions = useMemo(
    () => toGroupedSelectOptions(withoutCampaignStart(eventGroups)),
    [eventGroups],
  );

  const { mutate: createSegment, isPending } = useCreateSegment();

  const markCompleted = useCallback((step: SegmentStep) => {
    setCompletedSteps((prev) => {
      if (prev.has(step)) return prev;
      const next = new Set(prev);
      next.add(step);
      return next;
    });
  }, []);

  const handleStepClick = useCallback(
    (id: string) => {
      if (id === activeStep) return;
      if (!completedSteps.has(id as SegmentStep)) return;
      setActiveStep(id as SegmentStep);
    },
    [activeStep, completedSteps],
  );

  const handleDetailsContinue = useCallback(() => {
    form
      .validateFields(["name"])
      .then(() => {
        const { name, membership_ttl_days } = form.getFieldsValue(["name", "membership_ttl_days"]);
        setSummary((prev) => ({ ...prev, name, membership_ttl_days }));
        markCompleted("details");
        setActiveStep("membershipRule");
      })
      .catch(() => {});
  }, [form, markCompleted]);

  const onFinish = useCallback(
    (values: FormValues) => {
      const resolvedName = values.name ?? summary.name ?? "";
      const ttlDays =
        values.membership_ttl_days != null && values.membership_ttl_days !== ""
          ? parseInt(values.membership_ttl_days, 10)
          : null;

      // A membership rule is created whenever a catalog event is chosen. The
      // backend derives a stable rule_id from the resolved trigger key — we never send one.
      const hasRule = Boolean(values.event_key);
      const payload: SegmentIn = {
        name: resolvedName,
        description: values.description ?? "",
        membership_ttl_days: ttlDays,
        membership_rules: hasRule
          ? [
              {
                event_key: values.event_key!,
                field_checks:
                  values.field_check_field && values.field_check_operator
                    ? [
                        {
                          field: values.field_check_field,
                          operator: values.field_check_operator as FieldOperator,
                          value: values.field_check_value ?? null,
                        },
                      ]
                    : [],
              },
            ]
          : [],
      };

      setSummary((prev) => ({ ...prev, name: resolvedName }));
      createSegment(payload, {
        onSuccess: () => {
          navigate(NAVIGATION_ROUTES.DYNAMIC_BONUS_SEGMENTS);
        },
      });
    },
    [createSegment, summary, navigate],
  );

  const name = summary.name || getEmpty();
  const ttlDays = summary.membership_ttl_days || getEmpty();
  const eventLabel = watchedEventKey
    ? displayNameFor(eventGroups, watchedEventKey)
    : getEmpty();

  return (
    <>
      <FeatureFlagGuard feature="dynamicBonuses" mode="route">
        <div className={wizardStyles.page}>
          <div className={wizardStyles.header}>
            <button
              type="button"
              className={wizardStyles.backButton}
              onClick={() => navigate(NAVIGATION_ROUTES.DYNAMIC_BONUS_SEGMENTS)}
              aria-label={AppStrings.PAM_SEGMENTS_BACK_TO_SEGMENTS}
            >
              <span className={wizardStyles.backButtonIcon}>
                <LeftOutlined />
              </span>
            </button>
          </div>

          <div className={wizardStyles.content}>
            <div className={wizardStyles.wizardCard}>
              <WizardStepper
                steps={getSteps()}
                activeStepId={activeStep}
                completedStepIds={Array.from(completedSteps)}
                onStepClick={handleStepClick}
                idPrefix="create-segment"
                ariaLabel={AppStrings.PAM_SEGMENTS_WIZARD_ARIA}
              />

              <div className={wizardStyles.stepPanel}>
                <Form form={form} onFinish={onFinish} preserve className={styles.form}>
                  {activeStep === "details" && (
                    <>
                      <Form.Item
                        label={AppStrings.NAME}
                        name="name"
                        rules={[
                          {
                            required: true,
                            message: AppStrings.PAM_DYNAMIC_BONUS_NAME_REQUIRED,
                          },
                        ]}
                      >
                        <Input placeholder={AppStrings.PAM_SEGMENTS_NAME_PLACEHOLDER} />
                      </Form.Item>

                      <Form.Item label={AppStrings.DESCRIPTION} name="description">
                        <Input
                          placeholder={AppStrings.PAM_DYNAMIC_BONUS_DESCRIPTION_PLACEHOLDER}
                        />
                      </Form.Item>

                      <Form.Item
                        label={LOOKBACK_WINDOW_DAYS_LABEL}
                        name="membership_ttl_days"
                        getValueFromEvent={(e) =>
                          formatWholeNumberInput(e.target.value)
                        }
                      >
                        <Input placeholder={AppStrings.PAM_SEGMENTS_TTL_PLACEHOLDER} />
                      </Form.Item>

                      <div className={styles.stepFooter}>
                        <Button intent="primary" onClick={handleDetailsContinue}>
                          {AppStrings.CONTINUE}
                        </Button>
                      </div>
                    </>
                  )}

                  {activeStep === "membershipRule" && (
                    <>
                      <p className={styles.stepHint}>
                        {AppStrings.PAM_SEGMENTS_MEMBERSHIP_RULE_HINT}
                      </p>

                      {eventsError && (
                        <>
                          <p className={styles.listError}>
                            {AppStrings.PAM_DYNAMIC_BONUS_EVENTS_LOAD_ERROR}
                          </p>
                          <Button
                            intent="neutral"
                            variant="ghost"
                            onClick={() => refetchEvents()}
                          >
                            {AppStrings.RETRY}
                          </Button>
                        </>
                      )}

                      <Form.Item
                        label={AppStrings.PAM_DYNAMIC_BONUS_TRIGGER_EVENT}
                        name="event_key"
                      >
                        <Select
                          placeholder={
                            eventsLoading
                              ? AppStrings.PAM_SEGMENTS_TRIGGER_EVENTS_LOADING
                              : AppStrings.PAM_SEGMENTS_TRIGGER_EVENT_PLACEHOLDER
                          }
                          options={eventSelectOptions}
                          showSearch
                          disabled={eventsLoading || eventsError}
                        />
                      </Form.Item>

                      <div className={styles.fieldCheckRow}>
                        <Form.Item
                          label={AppStrings.PAM_SEGMENTS_FIELD_LABEL}
                          name="field_check_field"
                        >
                          <EventPropertyField
                            fields={fieldsForEvent(eventGroups, watchedEventKey ?? "")}
                            placeholder={AppStrings.PAM_SEGMENTS_FIELD_PLACEHOLDER}
                          />
                        </Form.Item>

                        <Form.Item
                          label={AppStrings.PAM_SEGMENTS_OPERATOR_LABEL}
                          name="field_check_operator"
                        >
                          <Select
                            placeholder={AppStrings.PAM_SEGMENTS_OPERATOR_PLACEHOLDER}
                            options={fieldOperatorOptions()}
                          />
                        </Form.Item>

                        <Form.Item label={AppStrings.VALUE} name="field_check_value">
                          <Input
                            placeholder={AppStrings.PAM_SEGMENTS_VALUE_PLACEHOLDER}
                          />
                        </Form.Item>
                      </div>

                      <div className={styles.stepFooter}>
                        <Button
                          intent="primary"
                          htmlType="submit"
                          loading={isPending}
                        >
                          {AppStrings.CREATE_SEGMENT}
                        </Button>
                        <Button
                          intent="neutral"
                          onClick={() => setActiveStep("details")}
                        >
                          {AppStrings.BACK}
                        </Button>
                      </div>
                    </>
                  )}
                </Form>
              </div>
            </div>

            <aside className={wizardStyles.summary}>
              <div className={wizardStyles.summarySection}>
                <p className={wizardStyles.summaryTitle}>
                  {AppStrings.PAM_SEGMENTS_SUMMARY_TITLE}
                </p>
                <dl className={wizardStyles.summaryList}>
                  <div className={wizardStyles.summaryRow}>
                    <dt className={wizardStyles.summaryLabel}>{AppStrings.NAME}</dt>
                    <dd className={wizardStyles.summaryValue}>{name}</dd>
                  </div>
                  <div className={wizardStyles.summaryRow}>
                    <dt className={wizardStyles.summaryLabel}>{LOOKBACK_WINDOW_DAYS_LABEL}</dt>
                    <dd className={wizardStyles.summaryValue}>{ttlDays}</dd>
                  </div>
                </dl>
              </div>
              <div className={wizardStyles.summarySection}>
                <p className={wizardStyles.summaryTitle}>
                  {AppStrings.PAM_SEGMENTS_WIZARD_STEP_MEMBERSHIP_RULE}
                </p>
                <dl className={wizardStyles.summaryList}>
                  <div className={wizardStyles.summaryRow}>
                    <dt className={wizardStyles.summaryLabel}>
                      {AppStrings.PAM_DYNAMIC_BONUS_TRIGGER_EVENT}
                    </dt>
                    <dd className={wizardStyles.summaryValue}>{eventLabel}</dd>
                  </div>
                </dl>
              </div>
            </aside>
          </div>
        </div>
      </FeatureFlagGuard>
    </>
  );
};

export default CreateSegmentPage;
